/* eslint-disable max-len */
import { useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import EmptyPageLoader from '../../../../components/EmptyPageLoader';
import TextInput from '../../../../components/input';
import PermissionDropdown from '../../../../components/PermissionDropdown';
import SideDrawer from '../../../../components/SideDrawer';
import StyledButton from '../../../../components/StyledButton';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import useForm from '../../../../customHooks/useForm';
import useToast from '../../../../customHooks/useToast';
import { ADMIN_CREATE_ROLES } from '../../../../graphql/admin_mutations';
import { GET_ALL_PERMISSIONS } from '../../../../graphql/admin_queries';
import useCustomQuery from '../../../../network/useCustomQuery';
import { convertSpacesToHyphen } from '../../../../utils/helpers';
import Styles from './CreateRole.module.scss';

interface ICreateRole{
  onClose:()=>void;
  refetch:any;
}
const CreateRole = ({ onClose, refetch }:ICreateRole) => {
  const Toast = useToast();
  const [getAllPermissions, allPermissionLoading, allPermissions] = useCustomQuery({
    query: GET_ALL_PERMISSIONS,
    onError: () => {},
    onData: () => {},
  });

  const [createRoles, { loading: createRolesLoading }] = useMutation<
  { createRole: any },
  any
>(ADMIN_CREATE_ROLES);

  const [permissionGroups, setPermissionGroups] = useState(null);
  const [checkAllPermissions, setCheckAllPermissions] = useState(false);
  useEffect(() => {
    getAllPermissions({
      variables: {
        platform: 'canada',
      },
    });
  }, []);

  const reformatPermissionToGroup = () => {
    // eslint-disable-next-line max-len
    const reformattedData = allPermissions?.getAllPermissions?.permissions?.reduce((acc, item) => {
      // Check if the group already exists in the accumulator
      if (!acc[item.group]) {
        // If not, create a new group with an empty array
        acc[item.group] = {
          group: item.group,
          permissions: [],
        };
      }
      // Push the item to the corresponding group
      acc[item.group]?.permissions.push(item);

      return acc;
    }, {});

    const dataValue = Object.values(reformattedData);

    setPermissionGroups(dataValue);
    // reformattedData?.map((list:any) => console.log('map data', list));
  };

  useEffect(() => {
    if (allPermissions?.getAllPermissions?.permissions) {
      reformatPermissionToGroup();
    }
  }, [allPermissions]);

  const {
    formValues, handleChange,
  } = useForm(
    {
      roleName: '',
      description: '',
    },
    {},
    Toast,
  );

  const handleUpdate = (id:string, group:string) => {
    const findSelectedValue = permissionGroups?.find((list:any) => list?.group === group);

    if (findSelectedValue) {
      const checkThroughPermissions = permissionGroups?.map((details) => {
        const updatedData = details?.group === findSelectedValue?.group ? { group: details?.group, permissions: details?.permissions?.map((permission) => (permission?.id === id ? { ...permission, hasPermission: !permission?.hasPermission } : { ...permission })) } : details;

        return updatedData;
      });

      setPermissionGroups(checkThroughPermissions);

      const allPermissionsAreTrue = checkThroughPermissions.every((obj) => obj.permissions.every((status) => status?.hasPermission === true));

      if (allPermissionsAreTrue) {
        setCheckAllPermissions(true);
      } else {
        setCheckAllPermissions(false);
      }
    }
  };

  const switchState = (state:boolean) => {
    const checkAllPermissions = permissionGroups?.map((list:any) => list?.group && {
      group: list?.group,
      permissions: list?.permissions?.map((perm:any) => ({
        ...perm,
        hasPermission: state,
      })),
    });
    setPermissionGroups(checkAllPermissions);
  };

  const handleCreate = async () => {
    const permissionIds = [];
    permissionGroups?.map((list:any) => list?.permissions?.filter((perm:any) => {
      if (perm?.hasPermission) {
        return permissionIds.push(perm?.id);
      }
      return '';
    }));

    const data = {
      displayName: formValues?.description,
      name: convertSpacesToHyphen(formValues?.roleName)?.toLowerCase(),
      permissionIds,
    };

    if (!createRolesLoading) {
      try {
        const res = await createRoles({
          variables: data,
        });

        if (res?.data?.createRole && res?.data?.createRole?.status === 200) {
          Toast.success(res?.data?.createRole?.message || 'Operation was successful');

          onClose();
          refetch?.();
        } else {
          Toast.error(res?.data?.createRole?.message || 'Operation failed');
        }
      } catch (e) {
        if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
          Toast.error(e.graphQLErrors[0].message);
        } else {
          Toast.error('Something went wrong');
        }
      }
    }
  };

  return (
    <div className={Styles.wrapper}>
      <SideDrawer onClose={onClose} title="Create New Role" subtitle="You can create a new user role here">
        {
          allPermissionLoading ? <EmptyPageLoader /> : (
            <div className={Styles.wrapper__inputAndPermissionContainer}>
              <TextInput name="roleName" isRequired value={formValues?.roleName} label="User Role Name" customOnChange={handleChange} width="100%" />
              <TextInput name="description" multiline rows={4} isRequired value={formValues?.description} label="Description" customOnChange={handleChange} width="100%" />
              <div className={Styles.wrapper__permissionContainer}>
                <div className={Styles.wrapper__permissionContainerTitleAndSwitch}>
                  <div className={Styles.wrapper__permissionContainerTitle}>Permissions</div>
                  <div>
                    <ToggleSwitch
                      checked={checkAllPermissions}
                      onChange={(e:any) => {
                        setCheckAllPermissions(e);
                        switchState(e);
                      }}
                    />

                  </div>
                </div>
                {
                  permissionGroups?.map((list:any, index:number) => <PermissionDropdown handleUpdateCheckStatus={(id:string, group:string) => handleUpdate(id, group)} data={list} key={index} />)
                }

              </div>
              <div className={Styles.wrapper__permissionBtn}>
                <StyledButton width="100%" name="Create" backgroundColor="rgba(89, 65, 169, 1)" borderRadius="4px" border="none" padding="16px" color="white" fontWeight="600" onClick={handleCreate} loading={createRolesLoading} disabled={createRolesLoading || !formValues?.roleName || !formValues?.description} />
              </div>
            </div>
          )
        }
      </SideDrawer>
    </div>

  );
};

export default CreateRole;
